import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { Card } from '@/components/ui'
import { Hud } from './Hud'
import './stage.css'
import './viewer.css'
import './panel.css'

/**
 * Three.js wiegt rund ein Megabyte. Es hat im Haupt-Bundle nichts zu suchen —
 * die Anmeldeseite und der Einladungsschirm brauchen es nie.
 *
 * Dieser Umweg hält die Bühne in einem eigenen Chunk, der erst geladen wird,
 * wenn die App-Shell steht, also nach dem Anmelden.
 *
 * Das HUD bleibt bewusst DRAUSSEN aus diesem Chunk: es ist SVG und CSS, wiegt
 * ein paar Kilobyte und steht damit sofort — auch während der 3D-Chunk noch
 * lädt und auch dann, wenn gar kein WebGL zur Verfügung steht. Ein leerer
 * schwarzer Grund im ersten Moment war genau der Eindruck, der die Oberfläche
 * leblos wirken liess.
 */
const StageBackdrop = lazy(() => import('./StageBackdrop'))

/*
  Das Bühnenfenster geht denselben Weg und aus demselben Grund. Es liegt sogar
  im SELBEN Chunk wie die Bühne — beide brauchen three, und zwei Chunks mit
  derselben Bibliothek wären ein Megabyte doppelt.
*/
const StageViewerLazy = lazy(() =>
  import('./StageViewer').then((m) => ({ default: m.StageViewer })),
)

/** Ruhiger Grund, solange der Chunk noch lädt. */
function BackdropPlaceholder() {
  return (
    <div className="backdrop" aria-hidden="true">
      <Hud />
      <div className="backdrop__scrim" />
    </div>
  )
}

/**
 * Das bedienbare Bühnenfenster.
 *
 * NICHT ZU VERWECHSELN MIT `<Backdrop />`: die Bühne ist Dekoration und nimmt
 * keine Zeigerereignisse an, dieses Fenster ist ein Messplatz und nimmt alle.
 * Wer die Bewegung drehen, anhalten oder an eine Stelle fahren will, braucht
 * dieses hier.
 */
export function StageViewer(props: {
  height?: number
  stuckHeight?: number
  initialOpen?: boolean
}) {
  return (
    <Suspense fallback={<div className="viewer viewer--loading" style={{ height: props.height }} />}>
      <StageViewerLazy {...props} />
    </Suspense>
  )
}

/**
 * Das Bühnenfenster als Kachel — auf jeder Seite dieselbe, beim Scrollen oben
 * angeheftet.
 *
 * WARUM ES EINE EIGENE KOMPONENTE IST. Der Block stand neunmal wortgleich in
 * den Seiten: Karte, Titel, Überzeile, Fenster, ein Satz darunter. Neunmal
 * dasselbe ist nicht neun Entscheidungen, sondern eine — und sie an einer
 * Stelle zu treffen ist der Unterschied zwischen „das Bühnenfenster klebt" und
 * „das Bühnenfenster klebt auf sieben von neun Seiten".
 *
 * WARUM ES HIER STEHT UND NICHT IN EINER EIGENEN DATEI: es benutzt die LAZY
 * geladene Fassung des Fensters. Eine eigene Datei müsste `./StageViewer`
 * unmittelbar einlesen — und zöge damit three ins Haupt-Bundle, für das die
 * ganze Konstruktion in dieser Datei gebaut wurde.
 *
 * WAS DAS ANHEFTEN LÖST. Eine Seite beginnt mit Überschrift und Vorspann, dann
 * kommt das Fenster, darunter Tabellen und Kurven. Wer die Tabelle liest, hat
 * die Figur längst nach oben aus dem Bild geschoben — und damit genau das
 * verloren, wofür sie da ist. Angeheftet bleibt sie stehen, und der Kopftext
 * darüber verschwindet, weil er einmal gelesen wird und die Figur immer.
 *
 * ANGEHEFTET IST SIE KLEINER, und das ist kein Detail. Ein Fenster in voller
 * Höhe, das dauerhaft oben klebt, nimmt auf einem Laptop die halbe Lesefläche —
 * man hätte die Figur gerettet und die Tabelle verloren. Beim Anheften fallen
 * Titel, Überzeile und Erläuterung weg und das Fenster geht auf die
 * Arbeitsgrösse zurück, in der die Figur noch zu untersuchen ist. Gelöst steht
 * es darüber: dort hat die Seite Platz, und das Motiv steht.
 *
 * DIE ERKENNUNG LÄUFT ÜBER EINEN MELDER, nicht über einen Scroll-Zuhörer. Ein
 * Zuhörer am Fenster feuert bei jedem Rad-Klick und müsste die Position der
 * Karte messen — ein Layoutzugriff je Ereignis, auf einer Seite mit einem
 * laufenden WebGL-Kontext daneben. Der Melder ist ein leerer Knoten unmittelbar
 * über der Karte: verlässt er nach oben das Bild, klebt die Karte.
 */
export interface StagePanelProps {
  /** Höhe im gelösten Zustand. */
  height?: number
  /**
   * Oben anheften. Aus, wo die Karte bereits in einer klebenden Spalte steht —
   * zwei ineinander geschachtelte Anheftungen sind eine zu viel.
   */
  sticky?: boolean
}

/**
 * Höhe im angehefteten Zustand.
 *
 * Sie ist die KLEINERE der beiden und trotzdem nicht klein: angeheftet soll man
 * die Figur noch untersuchen können und nicht nur wissen, dass es sie gibt. Der
 * gelöste Zustand liegt darüber — dort hat die Seite ohnehin Platz, und das
 * Motiv steht.
 */
const STUCK_HEIGHT = 400

export function StagePanel({ height = 760, sticky = true }: StagePanelProps) {
  const sentinel = useRef<HTMLDivElement>(null)
  const [stuck, setStuck] = useState(false)

  useEffect(() => {
    const node = sentinel.current
    if (!node || !sticky) return
    const watch = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting), {
      /* Der Melder gilt als draussen, sobald er unter die Kopfleiste rutscht —
         also genau dann, wenn die Karte anfängt zu kleben. Ohne diesen Rand
         käme die Meldung erst am oberen Fensterrand, und die Karte wäre schon
         seit achtzig Pixeln angeheftet. Der Wert gehört zu `top` in
         `panel.css`; wer den einen ändert, ändert den anderen mit. */
      rootMargin: '-84px 0px 0px 0px',
    })
    watch.observe(node)
    return () => watch.disconnect()
  }, [sticky])

  return (
    <>
      {sticky && <div ref={sentinel} className="stage-panel__sentinel" aria-hidden="true" />}
      <div
        className={`stage-panel${sticky ? ' stage-panel--sticky' : ''}${
          stuck ? ' stage-panel--stuck' : ''
        }`}
      >
        <Card>
          <StageViewer height={height} stuckHeight={STUCK_HEIGHT} />
        </Card>
      </div>
    </>
  )
}

export function Backdrop() {
  return (
    <Suspense fallback={<BackdropPlaceholder />}>
      <StageBackdrop />
    </Suspense>
  )
}
